<template>
  <RouterLink
    :to="{
      name: 'survey-member-feedback',
      params: {
        surveyId: feedback.survey.id,
        memberId:
          feedbackType == 'from'
            ? (feedback.sender?.id ?? feedback.anonymousSenderId)
            : feedback.receiver.id,
      },
    }"
    class="group relative flex flex-row items-center gap-6 overflow-hidden px-4 py-3"
  >
    <div class="flex shrink-0 grow items-center gap-2">
      <BaseMemberAvatar v-if="feedbackType == 'to'" :member="feedback.receiver" />
      <BaseMemberAvatar
        v-if="feedbackType == 'from' && feedback.sender"
        :member="feedback.sender"
      />
      <div
        v-if="feedbackType == 'from' && !feedback.sender"
        class="bg-secondary-container flex h-10 w-10 items-center justify-center rounded-full"
      >
        <BaseIcon size="24">person</BaseIcon>
      </div>
      <BaseMemberAvatar v-if="feedbackType == 'self'" :member="feedback.receiver" />
      <div class="flex flex-col">
        <p class="text-body-large flex flex-row items-center whitespace-nowrap">
          <FeedbackIcon :type="feedbackType" size="20" class="mr-1" />
          <span v-if="feedbackType == 'self'">{{ $t('common.selfFeedback') }}</span>
          <span v-else>
            {{ feedbackType == 'to' ? `${$t('common.to')}` : `${$t('common.from')}` }}
            {{ counterpartName }}
          </span>
        </p>
        <div class="text-body-medium text-on-surface-variant flex">
          {{ props.feedback.feedbackAmount }}
          {{ $t('common.answer', props.feedback.feedbackAmount) }}
        </div>
      </div>
    </div>
    <div class="">
      <p class="text-body-medium text-on-surface-variant line-clamp-2">
        {{ props.feedback.survey.title ?? feedbackTopicTitles }}
      </p>
    </div>
    <div class="text-on-surface-variant flex min-w-32 shrink-0 items-center justify-end gap-2">
      <TimeAgo class="text-label-small" :date="feedback.updatedAt" />
      <BaseIcon class="">chevron_right</BaseIcon>
    </div>
    <BaseStateLayer />
  </RouterLink>
</template>

<script setup lang="ts">
  import type { StructuredFeedbackItem } from '@/modules/view-feedback/composables/useFeedbackHistory'
  import { computed } from 'vue'
  import { useUserStore } from '../../../../stores/user.store'
  import BaseStateLayer from '../../../../core/components/base/state-layer/BaseStateLayer.vue'
  import { RouterLink } from 'vue-router'
  import TimeAgo from '@/core/components/base/time-ago/TimeAgo.vue'
  import BaseMemberAvatar from '../../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import FeedbackIcon from '../../../../core/components/feedback-icon/FeedbackIcon.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { memberAbbreviatedName } from '@/core/models/member/member-name'

  interface Props {
    feedback: StructuredFeedbackItem
  }

  const props = defineProps<Props>()

  const userStore = useUserStore()

  const feedbackType = computed(() => {
    if (
      props.feedback.sender?.id === userStore.user.id &&
      props.feedback.receiver.id === userStore.user.id
    ) {
      return 'self'
    } else if (props.feedback.sender?.id === userStore.user.id) {
      return 'to'
    } else {
      return 'from'
    }
  })

  const counterpartName = computed(() => {
    if (feedbackType.value == 'to') {
      return memberAbbreviatedName(props.feedback.receiver)
    }
    return props.feedback.sender ? memberAbbreviatedName(props.feedback.sender) : 'Anonym'
  })

  const feedbackTopicTitles = computed(() => {
    return props.feedback.survey.feedbackTopics.map((topic) => topic.title).join(', ')
  })
</script>
